import { inject, Injectable } from '@angular/core';
import { forkJoin, from, type Observable, of } from 'rxjs';
import { concatMap, map, switchMap, toArray } from 'rxjs/operators';

import { type ContestModel } from '../../models/Contest';
import { type ContestFilters } from '../../models/ContestFilters';
import { type ContestSort } from '../../models/ContestSort';
import {
  type ContestTemplate,
  type PoolTemplate,
  type RoundTemplate,
} from '../../models/ContestTemplate';
import { type DancerFilters, DancerModel } from '../../models/Dancer';
import { JudgeModel } from '../../models/Judge';
import { type GlobalLeaderboardEntry } from '../../models/Leaderboard';
import { type LeaderboardScoreFilter } from '../../models/LeaderboardScoreFilter';
import { OrganizerModel } from '../../models/Organizer';
import { type ParticipantFilters, type ParticipantModel } from '../../models/Participant';
import { type PoolModel } from '../../models/Pool';
import { type PoolSort } from '../../models/PoolSort';
import { type RoundModel, type RoundPoolData } from '../../models/Round';
import { type RoundSort } from '../../models/RoundSort';
import { type ScoreFilters, type ScoreModel } from '../../models/Score';
import { type ScoreSort } from '../../models/ScoreSort';
import { type ContestStatus, type ParticipantStatus } from '../../models/Status';
import { ContestRole, type UserFilters } from '../../models/User';
import { type DancerRole } from '../models/DancerRole';

import { ContestApiService } from './contest.api.service';
import { PoolApiService } from './pool.api.service';
import { RoundApiService } from './round.api.service';
import { ScoringControllerService } from './scoring-controller.service';

@Injectable({
  providedIn: 'root',
})
export class ContestService {
  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly contestApiService = inject(ContestApiService);
  private readonly roundApiService = inject(RoundApiService);
  private readonly poolApiService = inject(PoolApiService);
  private readonly scoringControllerService = inject(ScoringControllerService);

  // =============================================================================
  // Public Methods
  // =============================================================================

  resetScoringConfig(contestId: string): Observable<ContestModel> {
    return this.scoringControllerService.deleteContestOverride(contestId);
  }

  resetRoundScoringConfig(roundId: string): Observable<RoundModel> {
    return this.scoringControllerService.deleteRoundOverride(roundId);
  }

  resetPoolScoringConfig(poolId: string): Observable<PoolModel> {
    return this.scoringControllerService.deletePoolOverride(poolId);
  }

  getContestList(options?: {
    filters?: ContestFilters;
    sort?: ContestSort;
  }): Observable<ContestModel[]> {
    return this.contestApiService.getAll(options).pipe(map((res) => res.data));
  }

  getContestById(id: string): Observable<ContestModel> {
    return this.contestApiService.getById(id);
  }

  createContest(contest: Omit<ContestModel, 'id'>): Observable<ContestModel> {
    return this.contestApiService.create(contest);
  }

  updateContest(id: string, contest: Partial<ContestModel>): Observable<ContestModel> {
    return this.contestApiService.update(id, contest);
  }

  removeContest(id: string): Observable<void> {
    return this.contestApiService.delete(id);
  }

  deleteContestList(contests: ContestModel[]): Observable<void[]> {
    if (contests.length === 0) return of([]);
    const deleteOps = contests.map((c) => this.removeContest(c.id));
    return forkJoin(deleteOps);
  }

  updateContestStatus(
    id: string,
    status: ContestStatus,
    skipStatusCheck = false,
  ): Observable<ContestModel> {
    return this.contestApiService.updateStatus(id, status, skipStatusCheck);
  }

  updateContestListStatus(
    contests: ContestModel[],
    status: ContestStatus,
    skipStatusCheck = false,
  ): Observable<ContestModel[]> {
    const updateObservables = contests.map((contest) =>
      this.updateContestStatus(contest.id, status, skipStatusCheck),
    );
    return forkJoin(updateObservables);
  }

  getParticipantList(
    contestId: string,
    options?: {
      filters?: UserFilters;
    },
  ): Observable<ParticipantModel[]> {
    return this.contestApiService.getParticipantList(contestId, options);
  }

  getDancerListOfContest(
    contestId: string,
    options?: {
      filters?: DancerFilters;
    },
  ): Observable<DancerModel[]> {
    const filters = options?.filters;
    return this.contestApiService
      .getParticipantList(contestId, {
        filters: {
          contestRole: ContestRole.Dancer,
          dancerRole: filters?.dancerRole,
          bib: filters?.bib,
        } as ParticipantFilters,
      })
      .pipe(
        map((participants) =>
          participants.map((p) => {
            const dancer = new DancerModel();
            Object.assign(dancer, p);
            if (p.user) {
              dancer.username = p.user.username;
              dancer.systemRole = p.user.systemRole;
              dancer.isShadow = p.user.isShadow;
              dancer.firstName = p.user.firstName;
              dancer.lastName = p.user.lastName;
              dancer.email = p.user.email;
            }
            return dancer;
          }),
        ),
      );
  }

  getJudgeListOfContest(contestId: string): Observable<JudgeModel[]> {
    return this.contestApiService
      .getParticipantList(contestId, {
        filters: { contestRole: ContestRole.Judge } as ParticipantFilters,
      })
      .pipe(
        map((participants) =>
          participants.map((p) => {
            const judge = new JudgeModel();
            Object.assign(judge, p);
            if (p.user) {
              judge.username = p.user.username;
              judge.systemRole = p.user.systemRole;
              judge.isShadow = p.user.isShadow;
              judge.firstName = p.user.firstName;
              judge.lastName = p.user.lastName;
              judge.email = p.user.email;
            }
            return judge;
          }),
        ),
      );
  }

  getOrganizerListOfContest(contestId: string): Observable<OrganizerModel[]> {
    return this.contestApiService
      .getParticipantList(contestId, {
        filters: { contestRole: ContestRole.Organizer } as ParticipantFilters,
      })
      .pipe(
        map((participants) =>
          participants.map((p) => {
            const organizer = new OrganizerModel();
            Object.assign(organizer, p);
            if (p.user) {
              organizer.username = p.user.username;
              organizer.systemRole = p.user.systemRole;
              organizer.isShadow = p.user.isShadow;
              organizer.firstName = p.user.firstName;
              organizer.lastName = p.user.lastName;
              organizer.email = p.user.email;
            }
            return organizer;
          }),
        ),
      );
  }

  getPoolListOfContest(
    contestId: string,
    options?: {
      filters?: ContestFilters;
      sort?: PoolSort;
    },
  ): Observable<PoolModel[]> {
    return this.contestApiService.getPoolList(contestId, options);
  }

  getRoundListOfContest(
    contestId: string,
    options?: {
      filters?: ContestFilters;
      sort?: RoundSort;
    },
  ): Observable<RoundModel[]> {
    return this.contestApiService.getRoundList(contestId, options);
  }

  getScoreListOfContest(
    contestId: string,
    options?: {
      filters?: ScoreFilters;
      sort?: ScoreSort;
    },
  ): Observable<ScoreModel[]> {
    return this.contestApiService.getScoreList(contestId, options);
  }

  getGlobalLeaderboard(
    contestId: string,
    filters?: LeaderboardScoreFilter,
  ): Observable<GlobalLeaderboardEntry[]> {
    return this.contestApiService.getGlobalLeaderboard(contestId, filters);
  }

  addUsersToContest(
    contestId: string,
    users: {
      email: string;
      contestRole: ContestRole;
      bib?: number;
      dancerRole?: DancerRole;
      firstName?: string;
      lastName?: string;
    }[],
  ): Observable<void[]> {
    return this.contestApiService.addUsersToContest(contestId, users);
  }

  removeParticipantsFromContest(contestId: string, participantIds: string[]): Observable<void> {
    return this.contestApiService.removeParticipantsFromContest(contestId, participantIds);
  }

  removeParticipantFromContest(contestId: string, participantId: string): Observable<void> {
    return this.contestApiService.removeParticipantFromContest(contestId, participantId);
  }

  updateParticipantStatus(
    contestId: string,
    participantId: string,
    status: ParticipantStatus,
  ): Observable<ParticipantModel> {
    return this.contestApiService.updateParticipantStatus(contestId, participantId, status);
  }

  updateUserListStatus(
    contestId: string,
    users: ParticipantModel[],
    status: ParticipantStatus,
  ): Observable<ParticipantModel[]> {
    const updateObservables = users.map((user) =>
      this.updateParticipantStatus(contestId, user.id, status),
    );
    return forkJoin(updateObservables);
  }

  addScoresToContest(_contestId: string, _scores: ScoreModel[]): Observable<void> {
    return of(void 0);
  }

  addRoundsToContest(_contestId: string, _roundIds: string[]): Observable<void> {
    return of(void 0);
  }

  addPoolsToContest(_contestId: string, _poolIds: string[]): Observable<void> {
    return of(void 0);
  }

  getContestTemplates(): Observable<ContestTemplate[]> {
    return this.contestApiService.getContestTemplates();
  }

  applyContestTemplate(contestId: string, template: ContestTemplate): Observable<void> {
    // Step 1: Fetch existing rounds and pools
    const fetchStructure$ = forkJoin({
      rounds: this.contestApiService.getRoundList(contestId),
      pools: this.contestApiService.getPoolList(contestId),
    });

    // Step 2: Delete existing structure
    const deleteStructure$ = fetchStructure$.pipe(
      switchMap(({ rounds, pools }: { rounds: RoundModel[]; pools: PoolModel[] }) => {
        const deleteOps: Observable<void>[] = [];

        // Delete pools first (they depend on rounds)
        if (pools && pools.length > 0) {
          deleteOps.push(
            ...pools.map((pool: PoolModel) =>
              this.poolApiService.delete(pool.id).pipe(map(() => void 0)),
            ),
          );
        }

        // Then delete rounds
        if (rounds && rounds.length > 0) {
          deleteOps.push(
            ...rounds.map((round: RoundModel) =>
              this.roundApiService.delete(round.id).pipe(map(() => void 0)),
            ),
          );
        }

        // If nothing to delete, return immediately
        if (deleteOps.length === 0) {
          return of(void 0);
        }

        // Execute all deletions in parallel
        return forkJoin(deleteOps).pipe(map(() => void 0));
      }),
    );

    // Step 3: Update contest configuration
    const updateContest$ = this.updateContest(contestId, {
      colorSchema: template.contestConfig.colorSchema,
    });

    // Step 4: Create new structure from template
    const createStructure$ =
      !template.rounds || template.rounds.length === 0
        ? of(void 0)
        : from(template.rounds).pipe(
            concatMap((roundTemplate: RoundTemplate, index: number) => {
              return this.contestApiService
                .createRound({
                  name: roundTemplate.name,
                  contestId: contestId,
                  position: index,
                  scoringConfig: roundTemplate.scoringConfig,
                })
                .pipe(
                  concatMap((createdRound: RoundModel) => {
                    if (!roundTemplate.pools || roundTemplate.pools.length === 0) {
                      return of(createdRound);
                    }
                    const poolRequests = (roundTemplate.pools || []).map(
                      (poolTemplate: PoolTemplate) =>
                        this.contestApiService.createPool({
                          name: poolTemplate.name,
                          color: poolTemplate.color,
                          contestId: contestId,
                          roundId: createdRound.id,
                          scoringConfig: poolTemplate.scoringConfig,
                        }),
                    );
                    return forkJoin(poolRequests).pipe(map(() => createdRound));
                  }),
                );
            }),
            toArray(),
            map(() => void 0),
          );

    // Chain all operations: delete -> update -> create
    return deleteStructure$.pipe(
      switchMap(() => updateContest$),
      switchMap(() => createStructure$),
    );
  }

  applyContestStructure(contestId: string, structure: RoundPoolData[]): Observable<void> {
    if (!structure || structure.length === 0) {
      return of(void 0);
    }

    return from(structure).pipe(
      concatMap((roundItem: RoundPoolData, index: number) => {
        return this.contestApiService
          .createRound({
            name: roundItem.round.name,
            contestId: contestId,
            position: index,
            // You might want to copy scoringConfig if it exists on roundItem.round
          })
          .pipe(
            concatMap((createdRound: RoundModel) => {
              if (!roundItem.pools || roundItem.pools.length === 0) {
                return of(createdRound);
              }
              const poolRequests = roundItem.pools.map((pool: PoolModel) =>
                this.contestApiService.createPool({
                  name: pool.name,
                  color: pool.color,
                  contestId: contestId,
                  roundId: createdRound.id,
                  // You might want to copy scoringConfig if it exists on pool
                }),
              );
              return forkJoin(poolRequests).pipe(map(() => createdRound));
            }),
          );
      }),
      toArray(),
      map(() => void 0),
    );
  }
}
